import './style.css';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useRolesStore from 'store/roles';
import useUsersStore from 'store/users';
import { msg, t } from '@lingui/core/macro';
import { Trans, useLingui } from '@lingui/react/macro';
import { debounce } from 'lodash';
import { createModal, confirm, RichTable, Tooltip } from '@thermon-platforms/thermon-ui-core';
import EditModal from './edit-modal';
import ChangePasswordModal from './change-password';
import { Link } from 'react-router-dom';


function Users() {
  const { _ } = useLingui();
  const navigate = useNavigate();
  const [rolesStore, rolesStoreActions] = useRolesStore();
  const [usersStore, usersStoreActions] = useUsersStore();
  const [filteredUsers, setFilteredUsers] = useState([]);

  // Make sure we have latest roles and users
  useEffect(() => {
    rolesStoreActions.query();
    usersStoreActions.query();
  }, []);

  // Guard against un-approved access
  useEffect(() => {
    if (!usersStoreActions.checkAccess('AccountManager', rolesStore.roles)) navigate('/settings');
  }, [usersStore])

  useEffect(() => {
    // Set initial, unfiltered state
    setFilteredUsers(usersStore.users);
  }, [usersStore]);

  // Update filtered users based on search
  // NOTE - debounce here to prevent rapid-fire changes from user typing
  const updateSearch = debounce(function (search) {
    setFilteredUsers(
      usersStore.users.filter(user =>
        // NOTE - this is a really coarse search by just searching the whole
        // model but there likely won't be many users and it's fast and easy
        JSON.stringify(user).toLocaleLowerCase().includes(search.toLocaleLowerCase())
      )
    );
  }, 200);

  function editUser(user) {
    const editModal = createModal(<EditModal user={user} />);
    editModal.open().catch(() => {/* no-op */});
  }

  function changePassword(user) {
    const changePasswordModal = createModal(<ChangePasswordModal user={user} />);
    changePasswordModal.open().catch(() => {/* no-op */});
  }

  async function deleteUser(user) {
    const result = await confirm({
      header: `Are you sure you want to remove ${user.fullName}?`,
    });

    if (result) {
      usersStoreActions.remove(user);
    }
  }

  const columns = [
    {name: t`Name`, key: 'fullName', sortable: true},
    {name: t`Username`, key: 'username', sortable: true},
    {name: t`Email`, key: 'email', sortable: true},
    {name: t`Role`, key: 'roles'},
    t`Actions`
  ];

  function UsersTableRow({row, index}) {
    return (
      <tr>
        <td>{row.fullName}</td>
        <td>{row.username || <>&ndash;</>}</td>
        <td>{row.email || <>&ndash;</>}</td>
        <td>
          {row.roles ? row.roles.map(id =>
            rolesStore.roles.find(role => role.id == id)?.name || id
          ).join(', ') : <>&mdash;</>}
        </td>
        <td className="actions">
          {usersStore.currentUser.id == row.id ?
            <Link
              to="/settings/my-account"
              className="button user"
              data-tooltip-content={_(msg`My Account`)}
              data-tooltip-id={`control-tooltip-row-${index}`}
            >
              <Trans>My Account</Trans>
            </Link>:
            <button
              className="edit"
              data-tooltip-content={_(msg`Edit`)}
              data-tooltip-id={`control-tooltip-row-${index}`}
              onClick={() => editUser(row)}
              disabled={row.username == 'admin'}
            >
              <Trans>Edit</Trans>
            </button>
          }
          {usersStore.currentUser.id != row.id && usersStoreActions.checkAccess('CanChangePassword', rolesStore.roles) ?
            <button
              className="password"
              data-tooltip-content={_(msg`Change Password`)}
              data-tooltip-id={`control-tooltip-row-${index}`}
              onClick={() => changePassword(row)}
              disabled={row.username == 'admin'}
            >
              <Trans>Change Password</Trans>
            </button>
          : null}
          <button
            className="delete danger"
            data-tooltip-content={_(msg`Delete`)}
            data-tooltip-id={`control-tooltip-row-${index}`}
            onClick={() => deleteUser(row)}
            disabled={usersStore.currentUser.id == row.id || row.username == 'admin'}
          >
            <Trans>Delete</Trans>
          </button>
          {/*
            NOTE - This shouldn't be necessary since there's already a tooltip
            container in the main view but it doesn't work after navigation.
            This fixes it so ¯\_(ツ)_/¯
          */}
          <Tooltip id={`control-tooltip-row-${index}`} />
        </td>
      </tr>
    )
  }

  return (
    <div className="view users">
      <header>
        <h2><Trans>User Management</Trans></h2>
      </header>
      <section>
        <div className="table-controls">
          <span className="search-input">
            <input
              type="text"
              autoComplete="off"
              className="search"
              placeholder={_(msg`Search Users`)}
              onChange={event => updateSearch(event.target.value)}
            />
          </span>
          <button
            className="add-user"
            data-tooltip-content={_(msg`Add User`)}
            data-tooltip-id="control-tooltip"
            onClick={() => editUser()}
          >
            <Trans>Add User</Trans>
          </button>
        </div>
        <RichTable
          columns={columns}
          rows={filteredUsers}
          loading={usersStore.loading}
          responsive={true}
          sortable={true}
          Row={UsersTableRow}
          items="users"
        />

      </section>
      <Tooltip id="control-tooltip" />
    </div>
  );
}

export default Users;
